import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
// @ts-ignore -- the shared UI package does not publish declarations for these helpers.
import { closeSpinner, openSpinner } from '@paytheory/pay-theory-ui';
import { AppContext } from '../../App';
import * as network from '../../network';
import { UserPool } from '../../network/API/types';
import {
  PARTNER_BETA_PORTAL_DEFAULT_FIRST_NAME,
  PARTNER_BETA_PORTAL_DEFAULT_LAST_NAME,
  PARTNER_BETA_PORTAL_LEARN_MORE_URL,
  buildPartnerPortalMigrationDismissal,
  getPartnerBetaPortalEmailSentMessage,
  getPartnerBetaPortalUrl,
  isPartnerBetaPortalUserAlreadyExistsError,
  partnerBetaPortalBannerMessage,
  partnerBetaPortalCreateErrorMessage,
  partnerBetaPortalDismissCta,
  partnerBetaPortalExistingAccountHeader,
  partnerBetaPortalExistingAccountMessage,
  partnerBetaPortalLearnMoreCta,
  partnerBetaPortalLoginCta,
  partnerBetaPortalMissingProfileMessage,
  partnerBetaPortalPermissions,
  resetAllPartnerPortalMigrationDismissals,
  setPartnerPortalMigrationDismissal,
  shouldShowPartnerPortalMigrationPrompt,
} from './model';

declare global {
  interface Window {
    /** QA hook for clearing beta-banner dismissals in the current browser. */
    __resetPortalMigrationPrompt?: () => void;
  }
}

/**
 * Announces the beta partner portal and provisions the current partner admin
 * when they choose to continue. Dismissal is deliberately browser-local and
 * expires after 24 hours so the rollout prompt remains discoverable.
 */
const PartnerPortalMigrationPrompt = () => {
  const { userProfile, ErrorMessage, SuccessMessage } = useContext(AppContext);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [existingAccountUrl, setExistingAccountUrl] = useState<string | null>(
    null,
  );
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const modalCloseButtonRef = useRef<HTMLButtonElement>(null);
  const partner = process.env.REACT_APP_PARTNER?.trim();
  const email = userProfile.email?.trim();
  // Cognito profiles are not guaranteed to carry name attributes, so fall back
  // to a generic display name rather than blocking provisioning. The user can
  // correct it in the new portal.
  const firstName =
    userProfile.firstName?.trim() || PARTNER_BETA_PORTAL_DEFAULT_FIRST_NAME;
  const lastName =
    userProfile.lastName?.trim() || PARTNER_BETA_PORTAL_DEFAULT_LAST_NAME;

  const restorePrompt = useCallback(() => {
    setShowPrompt(shouldShowPartnerPortalMigrationPrompt({ email, partner }));
  }, [email, partner]);

  useEffect(() => {
    restorePrompt();
  }, [restorePrompt]);

  useEffect(() => {
    if (!existingAccountUrl) return undefined;

    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    modalCloseButtonRef.current?.focus();

    return () => {
      // Return keyboard users to the action that opened the dialog.
      if (previouslyFocusedElement?.isConnected) {
        previouslyFocusedElement.focus();
      }
    };
  }, [existingAccountUrl]);

  useEffect(() => {
    // Support and QA can re-show the banner without manipulating storage keys.
    window.__resetPortalMigrationPrompt = () => {
      resetAllPartnerPortalMigrationDismissals();
      restorePrompt();
    };

    return () => {
      delete window.__resetPortalMigrationPrompt;
    };
  }, [restorePrompt]);

  const dismissPrompt = () => {
    const dismissal = buildPartnerPortalMigrationDismissal(email, partner);
    if (dismissal) setPartnerPortalMigrationDismissal(dismissal);
    setShowPrompt(false);
  };

  const handleLoginClick = async () => {
    if (!email || !partner) {
      ErrorMessage(partnerBetaPortalMissingProfileMessage);
      return;
    }

    setIsCreating(true);
    openSpinner();

    try {
      const response = await network.users.createV2({
        input: {
          email,
          first_name: firstName,
          last_name: lastName,
          permissions: JSON.stringify(partnerBetaPortalPermissions),
          user_pool: UserPool.PARTNER,
        },
      });

      if (response.errors || !response.data) {
        const userAlreadyExists = response.errors?.some((error: unknown) =>
          isPartnerBetaPortalUserAlreadyExistsError(String(error)),
        );
        if (userAlreadyExists) {
          setExistingAccountUrl(getPartnerBetaPortalUrl());
        } else {
          ErrorMessage(partnerBetaPortalCreateErrorMessage);
        }
        return;
      }

      SuccessMessage(getPartnerBetaPortalEmailSentMessage(email));
    } catch {
      ErrorMessage(partnerBetaPortalCreateErrorMessage);
    } finally {
      closeSpinner();
      setIsCreating(false);
    }
  };

  const closeExistingAccountModal = () => setExistingAccountUrl(null);

  const handleModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeExistingAccountModal();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      ),
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];
    if (!firstFocusableElement || !lastFocusableElement) return;

    const movingBeforeDialog =
      event.shiftKey && document.activeElement === firstFocusableElement;
    const movingAfterDialog =
      !event.shiftKey && document.activeElement === lastFocusableElement;
    if (movingBeforeDialog || movingAfterDialog) {
      event.preventDefault();
      (movingBeforeDialog
        ? lastFocusableElement
        : firstFocusableElement
      ).focus();
    }
  };

  return (
    <>
      {showPrompt ? (
        <div className="partner-portal-migration-lane">
          <div className="partner-portal-migration-prompt">
            <p>
              {partnerBetaPortalBannerMessage}{' '}
              <a
                className="partner-portal-migration-learn-more"
                href={PARTNER_BETA_PORTAL_LEARN_MORE_URL}
                target="_blank"
                rel="noopener noreferrer">
                {partnerBetaPortalLearnMoreCta}
              </a>
            </p>
            <div className="partner-portal-migration-actions">
              <button
                className="partner-portal-migration-button partner-portal-migration-button-primary"
                disabled={isCreating}
                onClick={handleLoginClick}
                ref={loginButtonRef}
                type="button">
                {partnerBetaPortalLoginCta}
              </button>
              <button
                className="partner-portal-migration-button partner-portal-migration-button-text"
                disabled={isCreating}
                onClick={dismissPrompt}
                type="button">
                {partnerBetaPortalDismissCta}
              </button>
            </div>
          </div>
          <style>{bannerStyles}</style>
        </div>
      ) : null}

      {existingAccountUrl ? (
        <div
          aria-labelledby="partner-portal-migration-modal-title"
          aria-modal="true"
          className="partner-portal-migration-modal-backdrop"
          role="dialog">
          <div
            className="partner-portal-migration-modal"
            onKeyDown={handleModalKeyDown}>
            <h2 id="partner-portal-migration-modal-title">
              {partnerBetaPortalExistingAccountHeader}
            </h2>
            <p>
              {partnerBetaPortalExistingAccountMessage}{' '}
              <a
                href={existingAccountUrl}
                target="_blank"
                rel="noopener noreferrer">
                {existingAccountUrl}
              </a>
            </p>
            <button
              className="partner-portal-migration-button partner-portal-migration-button-primary"
              onClick={closeExistingAccountModal}
              ref={modalCloseButtonRef}
              type="button">
              Close
            </button>
          </div>
          <style>{modalStyles}</style>
        </div>
      ) : null}
    </>
  );
};

const bannerStyles = `
  .partner-portal-migration-lane {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 0;
    z-index: 50;
    pointer-events: none;
  }

  .partner-portal-migration-prompt {
    position: absolute;
    top: 12px;
    left: clamp(16px, 22vw, 320px);
    right: clamp(16px, 22vw, 320px);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 40px;
    padding: 0 8px 0 16px;
    border: 1px solid var(--pt-purple);
    border-radius: 8px;
    background: var(--purple-light);
    box-shadow: 1px 1px 1px var(--black-opaque-8);
    overflow: hidden;
    pointer-events: auto;
  }

  .partner-portal-migration-prompt p {
    margin: 0;
    margin-right: 24px;
    color: var(--black);
    font-size: 15px;
    line-height: 20px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .partner-portal-migration-learn-more {
    color: var(--pt-purple);
    text-decoration: underline;
    white-space: nowrap;
  }

  .partner-portal-migration-learn-more:hover {
    opacity: 0.75;
  }

  .partner-portal-migration-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .partner-portal-migration-button {
    border: 0;
    background: transparent;
    font-family: var(--secondary-font);
    text-transform: uppercase;
    cursor: pointer;
    transition:
      background 0.15s ease-in-out,
      color 0.15s ease-in-out,
      opacity 0.15s ease-in-out;
  }

  .partner-portal-migration-button:disabled {
    cursor: default;
    opacity: 0.64;
  }

  .partner-portal-migration-button-primary {
    box-sizing: border-box;
    height: 30px;
    min-height: 30px;
    padding: 0 12px;
    border-radius: 10px;
    color: var(--white);
    background: var(--pt-purple);
    font-size: 13px;
    line-height: 13px;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .partner-portal-migration-button-primary:not(:disabled):hover {
    background: var(--pt-purple-opaque-56);
  }

  .partner-portal-migration-button-text {
    padding: 0;
    font-size: 14px;
    line-height: 18px;
    color: var(--pt-purple);
    white-space: nowrap;
  }

  .partner-portal-migration-button-primary:focus-visible,
  .partner-portal-migration-button-text:focus-visible {
    outline: 2px solid var(--pt-purple);
    outline-offset: 2px;
  }

  .partner-portal-migration-button-text:not(:disabled):hover {
    text-decoration: underline;
  }

  @media only screen and (max-width: 760px) {
    .partner-portal-migration-prompt {
      left: 16px;
      right: 16px;
      height: auto;
      min-height: 40px;
      padding: 10px 12px;
      align-items: flex-start;
      flex-direction: column;
    }

    .partner-portal-migration-prompt p {
      margin-right: 0;
      white-space: normal;
    }

    .partner-portal-migration-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }

  @media only screen and (max-width: 600px) {
    .partner-portal-migration-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
`;

const modalStyles = `
  .partner-portal-migration-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 120;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.36);
  }

  .partner-portal-migration-modal {
    box-sizing: border-box;
    width: min(100%, 440px);
    padding: 24px;
    border-radius: 8px;
    background: var(--white);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    text-align: center;
  }

  .partner-portal-migration-modal h2 {
    margin: 0 0 12px;
    color: var(--black);
    font-size: 20px;
    line-height: 26px;
  }

  .partner-portal-migration-modal p {
    margin: 0 0 20px;
    color: var(--black);
    font-size: 15px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }

  .partner-portal-migration-modal a {
    color: var(--pt-purple);
    text-decoration: underline;
  }

  .partner-portal-migration-modal button {
    box-sizing: border-box;
    height: 30px;
    min-height: 30px;
    padding: 0 16px;
    border: 0;
    border-radius: 10px;
    color: var(--white);
    background: var(--pt-purple);
    font-family: var(--secondary-font);
    font-size: 13px;
    line-height: 13px;
    text-transform: uppercase;
    cursor: pointer;
  }

  .partner-portal-migration-modal button:hover {
    background: var(--pt-purple-opaque-56);
  }
`;

export default PartnerPortalMigrationPrompt;
